/**
 * Hook for auto-saving Ink App documents
 * Debounces block changes and auto-saves to database
 * Features: debounce, dirty tracking, error handling
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { saveInkDocument } from '../services/ink-document.service';
export function useInkAutoSave({ documentId, title, blocks, enabled = true, debounceMs = 5000, // 5 second debounce
onError, onSaveStart, onSaveComplete }) {
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const debounceTimerRef = useRef(null);
    const lastStateRef = useRef({ title, blocks });
    // Debounced save function (with optional force flag)
    const performSave = useCallback(async (forceEnabled = false) => {
        const isEnabled = forceEnabled || enabled;
        // Skip if not enabled or if documentId is empty/null/whitespace
        if (!isEnabled || !documentId || typeof documentId !== 'string' || documentId.trim() === '') {
            console.warn('[AutoSave] Save skipped:', { isEnabled, documentId, forceEnabled });
            return;
        }
        try {
            setIsSaving(true);
            onSaveStart?.();
            console.log(`[AutoSave] Saving document ${documentId}...`);
            const result = await saveInkDocument(documentId, title, blocks);
            if (result) {
                setLastSavedAt(new Date());
                setIsDirty(false);
                onSaveComplete?.();
                console.log(`[AutoSave] Document ${documentId} saved at ${new Date().toLocaleTimeString()}`);
            }
            else {
                throw new Error('Failed to save document');
            }
        }
        catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));
            console.error('[AutoSave] Error saving document:', err);
            onError?.(err);
        }
        finally {
            setIsSaving(false);
        }
    }, [documentId, title, blocks, enabled, onSaveStart, onSaveComplete, onError]);
    // Handle state changes with debounce
    useEffect(() => {
        // Check if state actually changed
        const stateChanged = lastStateRef.current.title !== title ||
            JSON.stringify(lastStateRef.current.blocks) !== JSON.stringify(blocks);
        if (!stateChanged)
            return;
        // Mark as dirty
        setIsDirty(true);
        lastStateRef.current = { title, blocks };
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        // Set new timer
        debounceTimerRef.current = setTimeout(() => {
            performSave();
        }, debounceMs);
        // Cleanup on unmount
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [title, blocks, debounceMs, performSave]);
    // Save immediately on unmount (if dirty)
    useEffect(() => {
        return () => {
            if (isDirty && debounceTimerRef.current && documentId && documentId.trim() !== '') {
                // Save synchronously if possible - only if we have a valid documentId
                performSave(true); // Force enabled for unmount save
            }
        };
    }, [isDirty, performSave, documentId]);
    // Manual save function - always saves regardless of enabled state
    const saveNow = useCallback(async () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        console.log('[AutoSave] Manual save triggered');
        return performSave(true); // Force enabled for manual save
    }, [performSave]);
    return {
        isSaving,
        isDirty,
        lastSavedAt,
        saveNow
    };
}
